import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { isBreathing } from '@/lib/breathing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Copy, Sparkles, X, ChevronDown } from 'lucide-react';

const GOALS = ['Recovery Reset', 'Longevity', 'Performance', 'Stress Reduction', 'General'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PURPLE = '#A78BFA';

const newDay = (i) => ({ day_index: i, activity_ids: [] });
const newWeek = (num) => ({ week_number: num, days: Array.from({ length: 7 }, (_, i) => newDay(i)) });
const normalizeWeeks = (weeks) => (weeks || []).map((w, wi) => {
  const byIdx = {};
  (w.days || []).forEach(d => { byIdx[d.day_index] = d; });
  return { week_number: wi + 1, days: Array.from({ length: 7 }, (_, i) => byIdx[i] ? { day_index: i, activity_ids: byIdx[i].activity_ids || [] } : newDay(i)) };
});

export default function RecoveryPrograms() {
  const [programs, setPrograms] = useState([]);
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    const [p, a] = await Promise.all([api.get('/recovery-programs'), api.get('/recovery/activities')]);
    setPrograms(p.data); setActivities(a.data.filter(x => !isBreathing(x)));
  };
  useEffect(() => { load(); }, []);

  const openBuilder = (prog) => {
    if (prog) { setEditing(prog.id); setForm({ ...prog, weeks: normalizeWeeks(prog.weeks.length ? prog.weeks : [newWeek(1)]) }); }
    else { setEditing(null); setForm({ name: '', goal: 'Recovery Reset', description: '', duration_weeks: 4, weeks: [newWeek(1)], is_template: true }); }
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Program name required');
    const payload = { ...form, duration_weeks: form.weeks.length };
    try {
      if (editing) await api.put(`/recovery-programs/${editing}`, payload);
      else await api.post('/recovery-programs', payload);
      toast.success('Program saved'); setOpen(false); load();
    } catch { toast.error('Failed to save'); }
  };
  const duplicate = async (id) => { await api.post(`/recovery-programs/${id}/duplicate`); toast.success('Duplicated'); load(); };
  const del = async (id) => { if (!confirm('Delete this program?')) return; await api.delete(`/recovery-programs/${id}`); toast.success('Deleted'); load(); };

  const setWeeks = (weeks) => setForm(f => ({ ...f, weeks }));
  const addWeek = () => setWeeks([...form.weeks, newWeek(form.weeks.length + 1)]);
  const dupWeek = (wi) => { const copy = JSON.parse(JSON.stringify(form.weeks[wi])); const weeks = [...form.weeks]; weeks.splice(wi + 1, 0, copy); setWeeks(weeks.map((w, i) => ({ ...w, week_number: i + 1 }))); };
  const delWeek = (wi) => { if (form.weeks.length === 1) return toast.error('Need at least one week'); setWeeks(form.weeks.filter((_, i) => i !== wi).map((w, i) => ({ ...w, week_number: i + 1 }))); };
  const dupDayToNext = (wi, di) => { if (di >= 6) return toast.error('No next day'); const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di + 1].activity_ids = [...weeks[wi].days[di].activity_ids]; setWeeks(weeks); };
  const clearDay = (wi, di) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].activity_ids = []; setWeeks(weeks); };
  const addAct = (wi, di, aid) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); if (!weeks[wi].days[di].activity_ids.includes(aid)) weeks[wi].days[di].activity_ids.push(aid); setWeeks(weeks); };
  const delAct = (wi, di, ai) => { const weeks = JSON.parse(JSON.stringify(form.weeks)); weeks[wi].days[di].activity_ids.splice(ai, 1); setWeeks(weeks); };

  return (
    <div className="space-y-4" data-testid="recovery-programs">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground max-w-xl">Build weekly recovery routines from your activities, then assign them to clients.</p>
        <Button onClick={() => openBuilder(null)} data-testid="add-recovery-program-btn" className="font-semibold" style={{ background: PURPLE, color: '#081726' }}><Plus className="w-4 h-4 mr-2" /> New Program</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.length === 0 && <div className="text-sm text-muted-foreground col-span-full py-10 text-center">No recovery programs yet.</div>}
        {programs.map(p => {
          const total = (p.weeks || []).reduce((s, w) => s + (w.days || []).reduce((ds, d) => ds + (d.activity_ids || []).length, 0), 0);
          return (
            <div key={p.id} className="regen-card-elevated p-5" data-testid={`recovery-program-${p.id}`}>
              <div className="flex items-start justify-between">
                <span className="pill text-[10px]" style={{ background: `${PURPLE}1a`, color: PURPLE }}>{p.goal || 'Recovery'}</span>
                <span className="text-[11px] font-mono text-muted-foreground">{p.weeks?.length || 0} wks · {total} sessions</span>
              </div>
              <div className="font-display font-bold text-lg mt-2">{p.name}</div>
              {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openBuilder(p)} data-testid={`edit-recovery-program-${p.id}`} className="border-[var(--c-hair)]"><Edit2 className="w-3 h-3 mr-1.5" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(p.id)} className="border-[var(--c-hair)]"><Copy className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(p.id)} className="border-red-500/30 text-red-400"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Recovery Program</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Program Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="rprog-name" placeholder="e.g. Recovery Reset" className="bg-transparent border-[var(--c-hair)]" /></div>
                <div>
                  <Label>Goal</Label>
                  <Select value={form.goal} onValueChange={v => setForm({ ...form, goal: v })}>
                    <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="rprog-goal"><SelectValue /></SelectTrigger>
                    <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-transparent border-[var(--c-hair)]" rows={2} /></div>
              <div className="space-y-3">
                {form.weeks.map((wk, wi) => (
                  <div key={wi} className="rounded-xl border border-[var(--c-hair)] overflow-hidden" data-testid={`rprog-week-${wi}`}>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--c-film)]">
                      <div className="font-display font-bold text-sm">Week {wk.week_number}</div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => dupWeek(wi)} className="h-7 border-[var(--c-hair)] text-xs"><Copy className="w-3 h-3 mr-1" /> Duplicate</Button>
                        <Button size="sm" variant="outline" onClick={() => delWeek(wi)} className="h-7 border-red-500/30 text-red-400 text-xs"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="divide-y divide-[var(--c-hair)]">
                      {wk.days.map((day, di) => (
                        <RecDayRow key={di} day={day} wi={wi} di={di} activities={activities}
                          addAct={addAct} delAct={delAct} dupDayToNext={dupDayToNext} clearDay={clearDay} />
                      ))}
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addWeek} data-testid="radd-week-btn" className="w-full border-dashed border-[var(--c-hair)] text-muted-foreground"><Plus className="w-4 h-4 mr-2" /> Add Week</Button>
              </div>
              <Button onClick={save} data-testid="save-recovery-program-btn" className="w-full font-semibold" style={{ background: PURPLE, color: '#081726' }}>Save Program</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecDayRow({ day, wi, di, activities, addAct, delAct, dupDayToNext, clearDay }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const aname = (id) => activities.find(a => a.id === id)?.name || 'Activity';
  return (
    <div className="px-4 py-2.5 flex items-start gap-3" data-testid={`rprog-day-${wi}-${di}`}>
      <div className="w-10 pt-1 text-xs font-bold text-muted-foreground uppercase">{DAYS[di]}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5">
          {day.activity_ids.length === 0 && <span className="text-xs text-muted-foreground/60 py-1">No recovery planned</span>}
          {day.activity_ids.map((aid, ai) => (
            <span key={ai} className="inline-flex items-center gap-1.5 rounded-lg pl-2 pr-1 py-1 text-xs" style={{ background: `${PURPLE}1a`, color: PURPLE }}>
              <Sparkles className="w-3 h-3" /><span>{aname(aid)}</span>
              <button onClick={() => delAct(wi, di, ai)} className="opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <div className="relative">
            <button onClick={() => setPickerOpen(o => !o)} data-testid={`add-activity-${wi}-${di}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)]"><Plus className="w-3 h-3" /> Activity <ChevronDown className="w-3 h-3" /></button>
            {pickerOpen && (
              <div className={`absolute z-50 ${di >= 5 ? 'bottom-full mb-1' : 'mt-1'} w-56 max-h-52 overflow-y-auto rounded-lg border border-[var(--c-hair)] bg-[var(--c-elev)] shadow-xl p-1`}>
                {activities.length === 0 && <div className="text-[11px] text-muted-foreground p-2">No activities. Add some in the Library.</div>}
                {activities.map(a => <button key={a.id} onClick={() => { addAct(wi, di, a.id); setPickerOpen(false); }} className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-[var(--c-film)]">{a.name} <span className="text-muted-foreground">· {a.category}</span></button>)}
              </div>
            )}
          </div>
          {day.activity_ids.length > 0 && <>
            <button onClick={() => dupDayToNext(wi, di)} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)] text-muted-foreground"><Copy className="w-3 h-3 inline mr-1" />Copy → next day</button>
            <button onClick={() => clearDay(wi, di)} className="text-[11px] px-2 py-1 rounded-md border border-[var(--c-hair)] hover:bg-[var(--c-film)] text-muted-foreground">Clear</button>
          </>}
        </div>
      </div>
    </div>
  );
}
